<template>
  <div class="w-full h-96 rounded-lg overflow-hidden mb-6">
    <div ref="mapContainer" class="w-full h-full"></div>
  </div>
</template>

<script setup>
const props = defineProps({
  route: { type: Array, required: true },
});
const mapContainer = ref(null);
let map = null;
let mapboxgl = null;

const config = useRuntimeConfig();
const MAPBOX_TOKEN = config.public.mapboxToken;

function getLngLat(location) {
  if (!location) return null;
  // Support new format: { name, coordinates } or { type, name, coordinates }
  if (Array.isArray(location.coordinates)) {
    return location.coordinates;
  }
  // Mapbox location object: geometry.coordinates or center
  if (location.geometry && Array.isArray(location.geometry.coordinates)) {
    return location.geometry.coordinates;
  }
  if (Array.isArray(location.center)) {
    return location.center;
  }
  return null;
}

function paintRoute() {
  if (!map || !props.route) return;
  // Remove old layers/sources
  const layers = map.getStyle().layers.map((l) => l.id);
  for (const id of layers) {
    if (
      id.startsWith("route-") ||
      id.startsWith("point-") ||
      id.startsWith("line-") ||
      id.startsWith("mainpoly-") ||
      id.startsWith("start-") ||
      id.startsWith("end-")
    ) {
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    }
  }
  // Collect all coordinates for fitBounds and for main polyline
  const allCoords = [];
  // Find main points (destination, activity, accommodation)
  const mainPoints = props.route.filter(
    (item) =>
      item.type === "destination" ||
      item.type === "accommodation" ||
      item.type === "activity"
  );
  // Paint points and lines in order
  props.route.forEach((item, idx) => {
    if (item.type === "line" && item.from && item.to) {
      // Paint custom line between two points
      const coords = [
        getLngLat(item.from.location),
        getLngLat(item.to.location),
      ].filter(Boolean);
      if (coords.length === 2) {
        map.addSource(`line-${idx}`, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coords,
            },
          },
        });
        map.addLayer({
          id: `line-${idx}`,
          type: "line",
          source: `line-${idx}`,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#6366f1",
            "line-width": 3,
            "line-dasharray": [1, 1],
          },
        });
      }
    } else if (
      item.type === "destination" ||
      item.type === "accommodation" ||
      item.type === "activity"
    ) {
      const coord = getLngLat(item.location);
      if (coord) {
        allCoords.push(coord);
        map.addSource(`point-${idx}`, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "Point", coordinates: coord },
            properties: { title: item.name, type: item.type },
          },
        });
        // Custom icon for each type (bigger)
        let el = document.createElement("div");
        el.style.width = "48px";
        el.style.height = "48px";
        el.style.background = "none";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        if (item.type === "accommodation") {
          el.innerHTML = `<svg width="40" height="40" fill="none" stroke="#f59e42" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></svg>`;
        } else if (item.type === "activity") {
          el.innerHTML = `<svg width="40" height="40" fill="#f43f5e" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#f43f5e" opacity="0.2"/><path d="M12 6v6l4 2" stroke="#f43f5e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        } else {
          // destination
          el.innerHTML = `<svg width="40" height="40" fill="#22c55e" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
        }
        const popup = new mapboxgl.Popup({ offset: 25 }).setText(
          `${item.name} (${item.type})`
        );
        new mapboxgl.Marker({ element: el })
          .setLngLat(coord)
          .setPopup(popup)
          .addTo(map);
      }
    }
  });
  // Draw special start and end markers
  if (mainPoints.length > 0) {
    const startCoord = getLngLat(mainPoints[0].location);
    if (startCoord) {
      let el = document.createElement("div");
      el.style.width = "56px";
      el.style.height = "56px";
      el.style.background = "none";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.innerHTML = `<svg width="48" height="48" viewBox="0 0 48 48"><g><path d="M12 44V8a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v36l-12-8-12 8z" fill="#22c55e" stroke="#166534" stroke-width="2"/><path d="M24 16v12" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/><circle cx="24" cy="12" r="2.5" fill="#fff"/></g></svg>`;
      new mapboxgl.Marker({ element: el })
        .setLngLat(startCoord)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText("Inicio"))
        .addTo(map);
    }
  }
  if (mainPoints.length > 1) {
    const endCoord = getLngLat(mainPoints[mainPoints.length - 1].location);
    if (endCoord) {
      let el = document.createElement("div");
      el.style.width = "56px";
      el.style.height = "56px";
      el.style.background = "none";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.innerHTML = `<svg width="48" height="48" viewBox="0 0 48 48"><g><path d="M36 4v36a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V4l12 8 12-8z" fill="#ef4444" stroke="#991b1b" stroke-width="2"/><path d="M24 32V20" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/><circle cx="24" cy="36" r="2.5" fill="#fff"/></g></svg>`;
      new mapboxgl.Marker({ element: el })
        .setLngLat(endCoord)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText("Fin"))
        .addTo(map);
    }
  }
  // Draw main polyline joining all main points
  if (allCoords.length > 1) {
    map.addSource("mainpoly-1", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: allCoords,
        },
      },
    });
    map.addLayer({
      id: "mainpoly-1",
      type: "line",
      source: "mainpoly-1",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#0ea5e9",
        "line-width": 7,
        "line-opacity": 0.25,
      },
    });
    // Fit map to all points
    const bounds = allCoords.reduce(
      (b, coord) => b.extend(coord),
      new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
    );
    map.fitBounds(bounds, { padding: 60, duration: 800 });
  } else if (allCoords.length === 1) {
    map.setCenter(allCoords[0]);
    map.setZoom(12);
  }
}

function initMap() {
  mapboxgl = window.mapboxgl;
  mapboxgl.accessToken = MAPBOX_TOKEN;
  map = new mapboxgl.Map({
    container: mapContainer.value,
    style: "mapbox://styles/mapbox/streets-v12",
    center: [-3.7038, 40.4168], // Default Madrid
    zoom: 5,
  });
  map.on("load", () => {
    paintRoute();
  });
}

onMounted(() => {
  if (!mapContainer.value) return;
  if (!window.mapboxgl) {
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
      script.onload = resolve;
      document.body.appendChild(script);
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
      document.head.appendChild(link);
    }).then(initMap);
  } else {
    initMap();
  }
});

watch(
  () => props.route,
  () => {
    if (map && map.loaded()) paintRoute();
  },
  { deep: true }
);
</script>

<style scoped>
.mapboxgl-canvas {
  border-radius: 0.5rem;
}
</style>
